import React, {forwardRef} from 'react'
import type {IconProps} from '@primer/octicons-react'
import Box from '../../Box'
import {Button, IconButton} from '../../Button'
import {Tooltip} from '../../TooltipV2'
import type {ButtonProps} from '../../Button'
import type {BetterSystemStyleObject, SxProp} from '../../sx'
import {merge} from '../../sx'
import {clsx} from 'clsx'

import styles from './TextInputInnerAction.module.css'
import {useFeatureFlag} from '../../FeatureFlags'
import {TEXT_INPUT_CSS_MODULES_FEATURE_FLAG} from './UnstyledTextInput'

type TextInputActionProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'aria-label' | 'size' | 'tooltipDirection'
> & {
  /** @deprecated Text input action buttons should only use icon buttons */
  children?: React.ReactNode
  /** Text that appears in a tooltip. If an icon is passed, this is also used as the label used by assistive technologies. */
  ['aria-label']?: string
  /** Position of tooltip. If no position is passed or defaults to "n" */
  tooltipDirection?: 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'nw'
  /** The icon to render inside the button */
  icon?: React.FunctionComponent<React.PropsWithChildren<IconProps>>
  /**
   * @deprecated Text input action buttons should only use the 'invisible' button variant
   * Determine's the styles on a button one of 'default' | 'primary' | 'invisible' | 'danger'
   */
  variant?: ButtonProps['variant']
} & SxProp

const invisibleButtonStyleOverrides = {
  paddingTop: '2px',
  paddingRight: '4px',
  paddingBottom: '2px',
  paddingLeft: '4px',
  position: 'relative',
  backgroundColor: 'transparent',
  color: 'fg.subtle',
  '&:hover, &:focus': {color: 'fg.default'},

  '&[data-component="IconButton"]': {
    width: 'var(--inner-action-size)',
    height: 'var(--inner-action-size)',
  },

  '@media (pointer: coarse)': {
    ':after': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      transform: 'translateY(-50%)',
      top: '50%',
      minHeight: '44px',
    },
  },
}

const ConditionalTooltip: React.FC<
  React.PropsWithChildren<{
    ['aria-label']?: string
    tooltipDirection?: 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'nw'
    children: React.ReactNode
  }>
> = ({'aria-label': ariaLabel, children, tooltipDirection}) => (
  <>
    {ariaLabel ? (
      <Tooltip
        text={ariaLabel}
        direction={tooltipDirection}
        sx={{
          /* inline-block is used to ensure the tooltip dimensions don't
             collapse when being used with `grid` or `inline` children */
          display: 'inline-block',
        }}
      >
        {children}
      </Tooltip>
    ) : (
      children
    )}
  </>
)

const TextInputAction = forwardRef<HTMLButtonElement, TextInputActionProps>(
  (
    {
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      tooltipDirection,
      children,
      icon,
      sx: sxProp,
      className,
      variant = 'invisible',
      ...rest
    },
    forwardedRef,
  ) => {
    const enabled = useFeatureFlag(TEXT_INPUT_CSS_MODULES_FEATURE_FLAG)

    const styleProps = enabled
      ? {className: clsx(variant === 'invisible' && styles.Invisible, className), sx: sxProp || {}}
      : {
          className,
          sx:
            variant === 'invisible'
              ? merge<BetterSystemStyleObject>(invisibleButtonStyleOverrides, sxProp || {})
              : sxProp || {},
        }

    if ((icon && !ariaLabel) || (!children && !ariaLabel)) {
      // eslint-disable-next-line no-console
      console.warn('Use the `aria-label` prop to provide an accessible label for assistive technology')
    }

    const accessibleLabel = ariaLabel
      ? {'aria-label': ariaLabel}
      : ariaLabelledBy
        ? {'aria-labelledby': ariaLabelledBy}
        : {
            'aria-label': '',
          }

    return (
      <Box as="span" className="TextInput-action" marginLeft={1} marginRight={1} lineHeight="0">
        {icon && !children && ariaLabel ? (
          <IconButton
            {...accessibleLabel}
            tooltipDirection={tooltipDirection ?? 's'}
            variant={variant}
            type="button"
            icon={icon}
            size="small"
            {...styleProps}
            {...rest}
            ref={forwardedRef}
          />
        ) : (
          <ConditionalTooltip aria-label={ariaLabel}>
            <Button variant={variant} type="button" {...styleProps} {...rest} ref={forwardedRef}>
              {children}
            </Button>
          </ConditionalTooltip>
        )}
      </Box>
    )
  },
)

export default TextInputAction
